import type { TCreateSlice } from "@/store";

import type { TAccountDTO } from "../accounts/accountTypes";
import { isNullable } from "@/utils/guards";

type TActionsPublicEditAccounts = {
    setAllAccounts: (accounts: TAccountDTO[]) => void;
    addAccount: (account: TAccountDTO) => void;
    updateAccount: (account: TAccountDTO) => void;
    deleteAccount: (accountId: number) => void;
    restoreAccount: (accountId: number) => void;
    onPasteAccounts: (event: ClipboardEvent) => void;
    resetAccountsEdited: () => void;
};

export type TStateEditAccounts = {
    allAccounts: TAccountDTO[];
    accountStateEdited: boolean;
    accountsToBeDeleted: number[];
    accountsEdited: boolean;
};

export type TSliceEditAccounts = TStateEditAccounts &
    TActionsPublicEditAccounts;

export const initialStateEditAccounts = {
    allAccounts: [],
    accountsToBeDeleted: [],
    accountStateEdited: false,
    accountsEdited: false,
} as const satisfies TStateEditAccounts;

export const createSliceEditAccounts: TCreateSlice<TSliceEditAccounts> =
    () => set => {
        const actionsPublic: TActionsPublicEditAccounts = {
            setAllAccounts: accounts => {
                set(
                    {
                        allAccounts: accounts,
                        accountsToBeDeleted: [],
                        accountStateEdited: false,
                        accountsEdited: false,
                    },
                    false,
                    "editAccounts/setAllAccounts",
                );
            },
            resetAccountsEdited: () => {
                set(
                    {
                        accountsEdited: false,
                    },
                    false,
                    "editAccounts/resetAccountsEdited",
                );
            },
            addAccount: account => {
                set(
                    state => ({
                        ...state,
                        allAccounts: [...state.allAccounts, account],
                        accountStateEdited: true,
                        accountsEdited: true,
                    }),
                    false,
                    "editAccounts/addAccount",
                );
            },
            onPasteAccounts: event => {
                const text = event.clipboardData?.getData("text");
                if (isNullable(text)) return;

                const rows = text.trim().split("\n");
                let newAccounts: TAccountDTO[] = [];

                for (const row of rows) {
                    const [code, account, type, subCategory, section] = row
                        .split("\t")
                        .map(col => col.trim());

                    if (
                        isNullable(code) ||
                        isNullable(account) ||
                        isNullable(type) ||
                        code === "" ||
                        account === "" ||
                        type === ""
                    )
                        continue;

                    const newAccount: TAccountDTO = {
                        id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
                        code,
                        account,
                        type: type.toLowerCase() as
                            | "income"
                            | "expense"
                            | "non-operating",
                        subcategory: isNullable(subCategory) ? "" : subCategory,
                        section: isNullable(section) ? "" : section,
                        includeInSummarizedDocument: false,
                        isEdited: true,
                    };

                    newAccounts = [...newAccounts, newAccount];
                }

                if (newAccounts.length > 0) {
                    set(
                        state => ({
                            ...state,
                            allAccounts: [...state.allAccounts, ...newAccounts],
                            accountStateEdited: true,
                            accountsEdited: true,
                        }),
                        false,
                        "editAccounts/onPasteAccounts",
                    );
                }
            },
            updateAccount: account => {
                set(
                    state => ({
                        ...state,
                        allAccounts: state.allAccounts.map(a =>
                            a.id === account.id
                                ? { ...account, isEdited: true }
                                : a,
                        ),
                        accountStateEdited: true,
                        accountsEdited: true,
                    }),
                    false,
                    "editAccounts/updateAccount",
                );
            },
            deleteAccount: (accountId: number) => {
                set(
                    state => ({
                        ...state,
                        accountsEdited: true,
                        accountsToBeDeleted: [
                            ...state.accountsToBeDeleted,
                            accountId,
                        ],
                    }),
                    false,
                    "editAccounts/deleteAccount",
                );
            },
            restoreAccount: (accountId: number) => {
                set(
                    state => ({
                        ...state,
                        accountsToBeDeleted: state.accountsToBeDeleted.filter(
                            id => id !== accountId,
                        ),
                    }),
                    false,
                    "editAccounts/restoreAccount",
                );
            },
        };

        return {
            ...initialStateEditAccounts,
            ...actionsPublic,
        };
    };
